/**
 * The template the brief for Claude is filled from (web/src/lib/taskPrompt.ts).
 * An admin edits it in the Integrations menu (server/taskPromptTemplate.ts);
 * each `<tag>` is replaced with that part of the task. Shared so the server
 * can hand back the default and the test can fill one without a browser.
 */

export const TASK_PROMPT_TAGS = [
  { tag: "parents", what: "the tasks it is part of, as a tree, root down, each with its notes" },
  { tag: "parent-concat", what: "the tasks it is part of, root down, each title and its notes run on, no tree" },
  { tag: "title", what: "its title" },
  { tag: "notes", what: "its notes" },
  { tag: "subtasks", what: "its sub-tasks, each with its status" },
  { tag: "dependencies", what: "the tasks it waits on, each with its status" },
  { tag: "dependants", what: "the tasks waiting on it, each with its status" },
  { tag: "repo", what: "its repo, or its parent's" },
  { tag: "automation", what: "what its repo's settings do with the work unasked: auto-commit, auto-merge, tests, push, deploy" },
  { tag: "id", what: "its id" },
  { tag: "link", what: "a link to it on this board" },
] as const;

export type TaskPromptTag = (typeof TASK_PROMPT_TAGS)[number]["tag"];
export type TaskPromptValues = Record<TaskPromptTag, string>;

/** What the brief always was, before it could be edited: the heading `<parents>` once carried is its label now. */
export const DEFAULT_TASK_PROMPT = "[This task is part of:\n<parents>]\n\n<title>\n\n<notes>\n\n<subtasks>\n\nTask: <link>";
export const TASK_PROMPT_MAX = 8000;

/** `<parents>`: a task's ancestors, root down, each with its notes indented under it —
 *  no heading of its own, a template gives it one (`[This task is part of:\n<parents>]`). */
function parentsTree(chain: { title: string; notes: string }[]): string {
  return chain
    .map((a, i) => {
      const head = `${"  ".repeat(i)}- ${a.title}`;
      const notes = a.notes.trim();
      return notes ? `${head}\n${notes.replace(/^/gm, "  ".repeat(i + 1))}` : head;
    })
    .join("\n");
}

/** `<parent-concat>`: the same ancestors, root down, each title with its notes under it, as
 *  they were written — no bullets, no indent — a blank line between one and the next. */
function parentsConcat(chain: { title: string; notes: string }[]): string {
  return chain.map((a) => [a.title, a.notes.trim()].filter(Boolean).join("\n")).join("\n\n");
}

/** Both tags a task's ancestors fill, from the chain root down. Shared because the
 *  server fills a brief too (feedback filed under a task). */
export function parentsValues(chain: { title: string; notes: string }[]): Pick<TaskPromptValues, "parents" | "parent-concat"> {
  return { parents: parentsTree(chain), "parent-concat": parentsConcat(chain) };
}

const TAG = /<([a-z]+(?:-[a-z]+)*)>/g;

/** A template's tags and what each is filled with: a tag not in it is not known. */
type Values = Readonly<Record<string, string>>;
const isKnown = (values: Values, name: string): boolean => Object.hasOwn(values, name);

/** Stands where an optional part was left out, so a line it leaves empty goes with it. */
const GONE = "\u0000";

/** Whether any known tag in `text` has something to say; null when it names none. */
function anyFilled(text: string, values: Values): boolean | null {
  let seen = false;
  for (const m of text.matchAll(TAG)) {
    if (!isKnown(values, m[1]!)) continue;
    if (values[m[1]!]!.trim()) return true;
    seen = true;
  }
  return seen ? false : null;
}

/**
 * Take the optional parts out of a template. Square brackets round a tag make
 * one — `[Parents: <parents>]` — and it becomes what is inside it while any
 * tag in there has something to say, and nothing while they are all empty, so
 * a label never stands beside nothing. Brackets with no known tag inside are
 * left as written (`[a link](…)`), also inside an optional part: the closing
 * bracket is the one that balances. A part may run over several lines and hold
 * another; a bracket never closed is left as written.
 */
function dropOptional(text: string, values: Values): string {
  let out = "";
  let at = 0;
  for (let open = text.indexOf("["); open !== -1; open = text.indexOf("[", at)) {
    let depth = 1;
    let end = open + 1;
    for (; end < text.length; end++) {
      if (text[end] === "[") depth++;
      else if (text[end] === "]" && --depth === 0) break;
    }
    out += text.slice(at, open);
    if (depth) {
      out += "[";
      at = open + 1;
      continue;
    }
    const inner = text.slice(open + 1, end);
    const filled = anyFilled(inner, values);
    out += filled === null ? `[${inner}]` : filled ? dropOptional(inner, values) : GONE;
    at = end + 1;
  }
  return out + text.slice(at);
}

/**
 * Replace every known `<tag>`; anything else in angle brackets is left as
 * written, so a template can still show Claude some HTML. A line that is only
 * an empty tag goes, and the blank lines around it close up, so a task with no
 * notes gets no gap where they would be. Only the template's own blank lines
 * are closed up, never ones inside the notes. Square brackets are the same for
 * more than a bare tag (`dropOptional`): `[Notes: <notes>]` goes, label and
 * all, and takes its line with it when nothing else is on it.
 */
export function fillTaskPrompt(template: string, values: TaskPromptValues): string {
  return fillTemplate(template, values);
}

/** Whether `template` names any of `tags`, by `<tag>`. */
export const namesTag = (template: string, tags: readonly string[]): boolean =>
  [...template.matchAll(TAG)].some((m) => tags.includes(m[1]!));

/**
 * `fillTaskPrompt()` for any template: its tags are `values`' keys, filled the
 * same way, with the same `[optional parts]`. Auto-fix's prompt is one too
 * (`autoFixPrompt()`, shared/repoSettings.ts).
 */
export function fillTemplate(template: string, values: Values): string {
  const lines: string[] = [];
  for (const raw of dropOptional(template.replaceAll(GONE, ""), values).split("\n")) {
    const line = raw.replaceAll(GONE, "");
    if (line !== raw && !line.trim()) continue;
    const only = /^\s*<([a-z]+(?:-[a-z]+)*)>\s*$/.exec(line)?.[1];
    if (only && isKnown(values, only) && !values[only]!.trim()) continue;
    if (!line.trim() && (lines.length === 0 || !lines[lines.length - 1]!.trim())) continue;
    lines.push(line);
  }
  while (lines.length && !lines[lines.length - 1]!.trim()) lines.pop();
  return lines
    .map((line) => line.replace(TAG, (whole, name: string) => (isKnown(values, name) ? values[name]! : whole)))
    .join("\n");
}
